import _chunk                     from 'lodash/chunk';
import View                       from '@widesk-ui/components/View';
import { Modal }                  from '@widesk-ui/components/modal/Modal';
import { message }                from '@widesk-ui/hooks/useMessage';
import ApiCollection              from '@widesk-core/models/ApiCollection';
import FeatureModel               from '@models/map/FeatureModel';
import PlaceModel                 from '@models/map/PlaceModel';
import MapModel                   from '@models/map/MapModel';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import { Theme }                  from '@/TokamapEditor/stores/themeStore';
import compareMapsByPosition      from '@/TokamapEditor/helpers/compareMapsByPosition';

const plural = (count: number, suffix = 's') => count > 1 ? suffix : '';

const ofTypes = (placeTypeIds: id[]) => placeTypeIds.length > 1 ? 'ces types' : 'ce type';

export async function proposeApplyTheme(theme: Theme, placeTypeIds: id[], projectId: id) {
	const models = await featuresOfPlaceTypes(placeTypeIds, projectId);
	const features = models.map(feature => feature.id);

	if (!features.length) return;

	const byMap = await countByMap(models);

	Modal.confirm({
		okText: 'Appliquer',
		cancelText: 'Laisser tel quel',
		title: `Appliquer "${theme.name}" aux formes déjà liées à ${ofTypes(placeTypeIds)} ?`,
		content: (
			<View gap="xs">
				{affectedShapes(byMap, features.length)}

				<View>
					Leur apparence propre sera remplacée par celle du thème ; elle est conservée et reviendra
					si vous les déliez.
				</View>

				<View bold>Cette action ne peut pas être annulée.</View>
			</View>
		),
		onOk: async () => {
			try {
				await patchFeatureThemes(features, `$:map:theme:${theme.id}`);

				refreshOpenMap(features, theme.id);

				message.success(`Thème appliqué à ${features.length} forme${plural(features.length)}.`);
			} catch (err) {
				message.exception(err);
			}
		},
	});
}

export async function proposeDetachTheme(theme: Theme, placeTypeIds: id[], projectId: id) {
	const models = (await featuresOfPlaceTypes(placeTypeIds, projectId))
		.filter(feature => String(feature.getResolvableId('theme')) === theme.id);
	const features = models.map(feature => feature.id);

	if (!features.length) return;

	const byMap = await countByMap(models);

	Modal.confirm({
		okText: 'Retirer',
		cancelText: 'Laisser tel quel',
		title: `Retirer "${theme.name}" des formes liées à ${ofTypes(placeTypeIds)} ?`,
		content: (
			<View gap="xs">
				{affectedShapes(byMap, features.length)}

				<View>Elles retrouveront leur apparence propre.</View>

				<View bold>Cette action ne peut pas être annulée.</View>
			</View>
		),
		onOk: async () => {
			try {
				await patchFeatureThemes(features, null);

				refreshOpenMap(features, undefined);

				message.success(`Thème retiré de ${features.length} forme${plural(features.length)}.`);
			} catch (err) {
				message.exception(err);
			}
		},
	});
}

type MapCount = { label: string; count: number; };

async function countByMap(features: FeatureModel[]): Promise<MapCount[]> {
	const counts = new Map<string, number>();

	features.forEach(feature => {
		const mapId = String(feature.getId('map') ?? '');

		counts.set(mapId, (counts.get(mapId) || 0) + 1);
	});

	const maps = new ApiCollection(MapModel);
	const mapIds = [...counts.keys()].filter(Boolean);

	if (mapIds.length) await maps.listBy(mapIds, 'id');

	return [...counts.entries()]
		.map(([mapId, count]) => ({ map: maps.find(map => String(map.id) === mapId), count }))
		.sort((a, b) => compareMapsByPosition(a.map ?? {}, b.map ?? {}))
		.map(({ map, count }) => ({ label: map?.label || 'Carte sans nom', count }));
}

const affectedShapes = (byMap: MapCount[], total: number) => (
	<View gap={2}>
		<View>
			{total} forme{plural(total)} concernée{plural(total)}
			{byMap.length > 1 ? ` sur ${byMap.length} cartes :` : ' :'}
		</View>

		<ul style={{ margin: 0, paddingLeft: 20 }}>
			{byMap.map(({ label, count }) => (
				<li key={label}>
					{label} — {count} forme{plural(count)}
				</li>
			))}
		</ul>
	</View>
);

/** `entityFilter[place]` se sérialise en un paramètre par valeur : au-delà, l'URL déborde. */
const PLACES_PER_REQUEST = 100;

/** Pas de `entityFilter[place.placeType]` : l'API rend une 500 dès qu'une feature du lot ne
 * pointe pas vers une place. */
async function featuresOfPlaceTypes(placeTypeIds: id[], projectId: id): Promise<FeatureModel[]> {
	const places = new ApiCollection(PlaceModel);

	places.setRequiredFilter('project', projectId);
	places.setFilter('placeType', placeTypeIds as never);
	await places.list();

	const placeIds = places.map(place => place.id);

	if (!placeIds.length) return [];

	const features = new Map<id, FeatureModel>();

	for (const chunk of _chunk(placeIds, PLACES_PER_REQUEST)) {
		const collection = new ApiCollection(FeatureModel);

		await collection.list({ params: {
			'entityFilter[place]': chunk,
			'map.project': projectId,
		} });

		collection.forEach(feature => features.set(feature.id, feature));
	}

	return [...features.values()];
}

const FEATURES_PER_BATCH = 200;

/** `ApiCollection.batch` vise `/features/batch` ; `BatchManager.patchMany` exigerait des filtres. */
async function patchFeatureThemes(featureIds: id[], themeUrn: string | null) {
	for (const chunk of _chunk(featureIds, FEATURES_PER_BATCH)) {
		const collection = new ApiCollection(
			FeatureModel,
			chunk.map(id => new FeatureModel({ id })),
		);

		await collection.batch({ method: 'patch' }, () => ({ theme: themeUrn }));
	}
}

/** Le lot est déjà écrit, et l'historique ne pourrait défaire que la carte ouverte. */
function refreshOpenMap(featureIds: id[], themeId: string | undefined) {
	const shapeIds = featureIds
		.map(featureId => currentEditorStore.findShapeFromFeatureId(Number(featureId))?.id)
		.filter((id): id is string => !!id);

	if (!shapeIds.length) return;

	currentEditorStore.get.setShapeThemeId(shapeIds, themeId, {
		skipSave: true,
		skipHistory: true,
		skipConfirm: true,
	});
}
